import * as React from 'react';
import type { ExtensionRegistration } from '../index.ts';
import type { Contribution } from './registry.ts';

// What a slot renders for its contributions, apart from React's rendering:
// one element per contribution, keyed by the registered component's name
// and its place, with `params` = the registration defaults merged with the
// slot's params. Plain `createElement`, no JSX, so Node's test runner can
// import it (host/registry.test.ts).

export interface SlotOptions {
  params?: unknown;
  children?: React.ReactNode;
  /** The slot's `wrap`: called with each contribution's element and its registration. */
  wrap?(node: React.ReactNode, registration: ExtensionRegistration): React.ReactNode;
}

/** The key a contribution is rendered under: the same with or without `wrap`. */
export const contributionKey = (reference: unknown, i: number) => `${(reference as { displayName?: string })?.displayName || '_'}${i}`;

/**
 * The contributions' elements. With `wrap`, each element is passed to it
 * and its result rendered in the element's place under the same key; the
 * element is the host's wrapped component, so whatever `wrap` draws stands
 * outside the contribution's error boundary. A `wrap` that returns the
 * element itself leaves the tree exactly as without it.
 */
export function slotNodes(contributions: readonly Contribution[], { params, children, wrap }: SlotOptions): React.ReactNode[] {
  return contributions.map((registration, i) => {
    const { component, reference, defaults = {} } = registration;
    const key = contributionKey(reference, i);
    const element = React.createElement(component as React.ComponentType<any>, {
      key,
      children,
      params: { ...defaults, ...(params as object) },
    });
    if (!wrap) return element;
    const wrapped = wrap(element, registration as ExtensionRegistration);
    if (wrapped === element) return element;
    if (React.isValidElement(wrapped)) return React.cloneElement(wrapped, { key });
    return React.createElement(React.Fragment, { key }, wrapped);
  });
}
